import React from 'react';
import PropTypes from 'prop-types';
import TreeSelect from 'antd/lib/tree-select';
import _ from 'lodash';
import 'antd/lib/select/style/index.less';
import 'antd/lib/tree-select/style/index.less';
import './style.less';
import FormLabel from '@material-ui/core/FormLabel';
import { fromJS } from 'immutable';

const { SHOW_PARENT } = TreeSelect;

const FormLocationSelector = ({
  locations,
  input,
  disabled,
  style,
  expanded = true,
  placeholder,
  meta,
}) => {
  const userVisibleLocations = locations.toArray();
  const selectedLocations = (input.value.toArray ? input.value.toArray() : input.value) || [];
  const unselectableLocations = _.differenceBy(
    selectedLocations,
    userVisibleLocations.map((location) => location.id)
  );
  const treeSelectOptions = [];

  const onChange = (selectedOptions) => {
    const actuallySelectedLocations = selectedOptions.map((option) => {
      if (option.includes('country-')) {
        const country = option.substr(8);
        return userVisibleLocations
          .filter(
            (locationObj) => locationObj.country === country || locationObj.countryCode === country
          )
          .map((locationObj) => locationObj.id);
      }
      if (option.includes('state-')) {
        const [, country, state] = option.split('-');
        return userVisibleLocations
          .filter(
            (locationObj) =>
              (locationObj.state === state && locationObj.country === country) ||
              (locationObj.stateCode === state && locationObj.countryCode === country)
          )
          .map((locationObj) => locationObj.id);
      }
      return option;
    });
    const inputLocations = _.flatten(actuallySelectedLocations || []).concat(unselectableLocations);

    input.onChange(fromJS(inputLocations));
  };

  const groupBy = (array, key, fallbackKey) =>
    array.reduce((objectsByKeyValue, obj) => {
      const value = obj[key] || obj[fallbackKey];
      if (value) {
        objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj); // eslint-disable-line no-param-reassign
      }
      return objectsByKeyValue;
    }, {});

  const locationsDataByCountry = groupBy(userVisibleLocations, 'countryCode', 'country');

  // Iterate through the location data and build a hierarchy of state within city
  if (locationsDataByCountry) {
    Object.keys(locationsDataByCountry)
      .sort()
      .forEach((countryKey) => {
        // Place to shove stuff for this country.
        const [firstCountryLocation] = locationsDataByCountry[countryKey];
        treeSelectOptions.push({
          title: firstCountryLocation.country || firstCountryLocation.countryCode,
          value: `country-${countryKey}`,
          key: `country-${countryKey}`,
          children: [],
        });

        // This country's items by state.
        const locationsByState = groupBy(locationsDataByCountry[countryKey], 'stateCode', 'state');

        // For each state.
        Object.keys(locationsByState)
          .sort()
          .forEach((stateKey) => {
            const [firstStateLocation] = locationsByState[stateKey];
            treeSelectOptions[treeSelectOptions.length - 1].children.push({
              title: firstStateLocation.state || firstStateLocation.stateCode,
              value: `state-${countryKey}-${stateKey}`,
              key: `state-${countryKey}-${stateKey}`,
              children: _.orderBy(locationsByState[stateKey], ['name'], ['asc']).map(
                (location) => ({
                  title: location.name,
                  value: location.id,
                  key: location.id,
                })
              ),
            });
          });

        const noStateLocations = locationsDataByCountry[countryKey].filter(
          (location) => !location.state && !location.stateCode
        );
        if (noStateLocations.length > 0) {
          _.orderBy(noStateLocations, ['name'], ['asc']).forEach((noStateLocation) => {
            treeSelectOptions[treeSelectOptions.length - 1].children.push({
              title: noStateLocation.name,
              value: noStateLocation.id,
              key: noStateLocation.id,
            });
          });
        }
      });
  }

  const noCountryLocations = userVisibleLocations.filter(
    (location) => !location.country && !location.countryCode
  );
  if (noCountryLocations.length > 0) {
    _.orderBy(noCountryLocations, ['name'], ['asc']).forEach((noCountryLocation) => {
      treeSelectOptions.push({
        title: noCountryLocation.name,
        value: noCountryLocation.id,
        key: noCountryLocation.id,
      });
    });
  }

  const manyOptions = userVisibleLocations.length > 100;

  const tProps = {
    value: selectedLocations.filter(
      (selectedLocation) => !unselectableLocations.includes(selectedLocation)
    ),
    treeData: treeSelectOptions,
    onChange,
    disabled,
    treeCheckable: true,
    treeDefaultExpandAll: expanded && !manyOptions,
    treeDefaultExpandedKeys:
      expanded && manyOptions && treeSelectOptions.map((option) => option.key),
    showCheckedStrategy: SHOW_PARENT,
    placeholder: placeholder || 'Select locations',
    filterTreeNode: (searchTerm, item) => {
      return (
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.value?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    },
    style: {
      width: 300,
      ...style,
    },
  };
  return (
    <div>
      <TreeSelect {...tProps} />
      {meta?.error && (
        <div>
          <FormLabel error style={{ fontSize: 12, marginTop: 5 }}>
            {meta.error}
          </FormLabel>
        </div>
      )}
    </div>
  );
};
FormLocationSelector.propTypes = {
  input: PropTypes.object,
  style: PropTypes.object,
  locations: PropTypes.object,
  disabled: PropTypes.bool,
  expanded: PropTypes.bool,
  placeholder: PropTypes.string,
  meta: PropTypes.object,
};

export default FormLocationSelector;
